/**
 * Dev shell: wires the sky renderer to the simulated orientation source.
 * On a real device this file will grow into the app shell (phase 12); for
 * now it exercises the phase 8/9/10 pipeline end to end in the browser:
 *   - GPU sky rendering (phase 7)
 *   - tap-to-identify with an info card
 *   - sky labels pinned to the projected star positions
 *   - compass HUD (heading / tilt / calibration hint)
 *   - search (autocomplete + results) and SeeIt-mode direction guidance
 *   - settings panel: display toggles, compass calibration UX, location
 *   - boot overlay, update-ready toast (PWA service worker)
 *   - camera AR layer (live video under a transparent sky overlay)
 */

import { registerSW } from "virtual:pwa-register";
import { CameraController, type CameraState } from "./ar/cameraController";
import { LocationService } from "./location/locationService";
import { OrientationFusion } from "./orientation/orientationFusion";
import {
  DeviceOrientationSource,
  motionPermissionSupported,
  requestMotionPermission,
  simulatorFallbackDue,
} from "./orientation/deviceOrientationSource";
import { CalibrationStore } from "./orientation/compassCalibration";
import { PreferencesStore } from "./settings/preferences";
import { GlSession } from "./render/glSession";
import { SkyRenderer } from "./render/skyRenderer";
import { SimulatedOrientation } from "./render/simOrientation";
import { deviceToEarthQ } from "./orientation/deviceOrientation";
import { createBodyStateCache, bodyColorData, bodySpriteData, bodyStateById } from "./render/dynamicBodies";
import { j2000ToOfDateRotation } from "./astro/engine";
import { nowObserverTime } from "./astro/time";
import type { ObserverLocation, ObserverTime } from "./astro/types";
import type { GeoLocation } from "./location/types";
import { pickAtPixel } from "./interact/picking";
import { seeItState, type SeeItState } from "./interact/seeIt";
import { calibrationOffsetForNorth } from "./interact/calibration";
import { describeObject } from "./catalog/catalog";
import { bodyDisplayInfo, bodyFromId, bodyPositionJ2000 } from "./catalog/dynamicObjects";
import { NoSatelliteProvider } from "./catalog/satellites";
import { resolveFovYDeg } from "./projection/fov";
import { deviceHeadingDeg, deviceTiltDeg, headingUsable } from "./ui/hud";
import { perspective } from "./render/mat4";
import { buildStarProjection, equatorialVector, transformDir } from "./render/skyFrame";
import { LabelLayer } from "./ui/labelLayer";
import { HudLayer } from "./ui/hudLayer";
import { InfoCard } from "./ui/infoCard";
import { bodyLabels, projectLabels, skyLabels } from "./ui/skyLabels";
import { DiagnosticsOverlay } from "./ui/diagnostics";
import { SearchPanel } from "./ui/searchPanel";
import { SeeItOverlay } from "./ui/seeItOverlay";
import { SettingsPanel } from "./ui/settingsPanel";
import { CalibrationOverlay } from "./ui/calibrationOverlay";

const canvas = document.querySelector<HTMLCanvasElement>("#sky");
if (!canvas) throw new Error("missing #sky canvas");

// --- boot overlay + toasts --------------------------------------------------
const boot = document.querySelector<HTMLElement>("#boot")!;
const bootStatus = boot.querySelector<HTMLElement>(".boot-status");
function bootError(message: string): void {
  if (bootStatus) {
    bootStatus.textContent = message;
    bootStatus.classList.add("boot-error");
  }
}
function hideBoot(): void {
  boot.classList.add("fading");
  window.setTimeout(() => boot.classList.add("hidden"), 450);
}

const toastEl = document.querySelector<HTMLElement>(".app-toast")!;
let rendererStartFailed = false;
function toast(message: string, action?: { label: string; run: () => void }): void {
  toastEl.replaceChildren(document.createTextNode(message));
  if (action) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.textContent = action.label;
    btn.addEventListener("click", () => {
      toastEl.style.display = "none";
      action.run();
    });
    toastEl.appendChild(btn);
  }
  toastEl.style.display = "flex";
  window.setTimeout(() => {
    toastEl.style.display = "none";
  }, action ? 15_000 : 5_000);
}

const updateSW = registerSW({
  onNeedRefresh() {
    toast("A new version of the sky is available.", { label: "Update", run: () => updateSW(true) });
  },
  onOfflineReady() {
    toast("Starlight Sky is ready to work offline.");
  },
});

const session = new GlSession(canvas);
if (!session.available) {
  bootError("WebGL2 is unavailable — nothing to render on this browser.");
}
// A lost context stops all rendering: surface it as an error state instead of
// leaving a frozen black canvas. The browser may restore the context later;
// allow the default restore path (do not preventDefault).
canvas.addEventListener("webglcontextlost", () => {
  console.error("[startup] WebGL context lost");
  bootError("The graphics context was lost — reload the app to restore the sky.");
});
let renderer: SkyRenderer | null = null;
try {
  renderer = new SkyRenderer(session);
} catch (err) {
  // Shader compile/link or buffer setup failure: surface it instead of
  // leaving the boot overlay on "loading the sky…" forever.
  console.error("[startup] SkyRenderer failed to initialize", err);
  bootError(
    "The sky renderer failed to start (graphics driver or WebGL2 shader issue). " +
      "Try reloading; if it persists, update your browser or enable WebGL2.",
  );
  // No renderer: keep the boot overlay as the error screen (frame() is
  // never scheduled below because rendererStartFailed is set).
  rendererStartFailed = true;
}
const sim = new SimulatedOrientation();
const prefs = new PreferencesStore(localStorage);
const calibration = new CalibrationStore(localStorage);
const locService = new LocationService(
  "geolocation" in navigator ? navigator.geolocation : null,
  localStorage,
);

// --- real device orientation (phase 7.1) -------------------------------------
// Prefer the browser's real orientation events; the simulator is only a
// desktop/dev fallback and never engages while genuine readings arrive.
const realOrientation = new DeviceOrientationSource({ target: window });
let fusion: OrientationFusion;
let simActive = false;
if (realOrientation.available) {
  fusion = new OrientationFusion(realOrientation, { smoothing: 0.7, calibration });
} else {
  simActive = true;
  sim.start();
  fusion = new OrientationFusion(sim, { smoothing: 0.7, calibration });
}
fusion.start();

// Desktop browsers declare the orientation events but never fire them: after
// the grace period, if no reading arrived and the sensors are not expected
// (no touch input, explicit denial, or granted-but-silent), fall back to the
// simulated source.
const MOTION_GRACE_MS = 3000;
const TOUCH_GRACE_MS = 12000;
const GRANTED_SILENT_MS = 15000;
let motionPermissionDenied = false;
let motionPermissionGranted = false;
let fallbackTimer: ReturnType<typeof setInterval> | null = null;
let orientationStartMs = performance.now();
let fallbackReason: string | null = simActive ? "no-sensor" : null;
function checkOrientationFallback(): void {
  if (simActive || !realOrientation.available) return;
  const due = simulatorFallbackDue({
    hasReading: fusion.status !== "unavailable",
    permissionDenied: motionPermissionDenied,
    permissionGranted: motionPermissionGranted,
    permissionRequestable: motionPermissionSupported(typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null),
    maxTouchPoints: "maxTouchPoints" in navigator ? navigator.maxTouchPoints : 0,
    graceElapsedMs: performance.now() - orientationStartMs,
    graceMs: MOTION_GRACE_MS,
    touchGraceMs: TOUCH_GRACE_MS,
    grantedGraceMs: GRANTED_SILENT_MS,
  });
  if (due) {
    fallbackReason = motionPermissionDenied
      ? "permission-denied"
      : motionPermissionGranted
        ? "granted-silent"
        : motionPermissionSupported(typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null)
          ? "requestable-timeout"
          : "no-reading";
    simActive = true;
    fusion.stop();
    sim.start();
    fusion = new OrientationFusion(sim, { smoothing: 0.7, calibration });
    fusion.start();
    console.warn(`[orientation] no device orientation readings; using simulated orientation (${fallbackReason})`);
    toast("Using simulated orientation — the sky is not tracking your phone.", {
      label: "Enable motion",
      run: () => void tryEnableRealOrientation(),
    });
    if (fallbackTimer) clearInterval(fallbackTimer);
    fallbackTimer = null;
    return;
  }
  const permanentlyFalse =
    fusion.status !== "unavailable" ||
    (motionPermissionSupported(typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null) &&
      !motionPermissionDenied &&
      !motionPermissionGranted);
  if (permanentlyFalse) {
    if (fallbackTimer) clearInterval(fallbackTimer);
    fallbackTimer = null;
  }
}
function armOrientationFallback(): void {
  if (fallbackTimer) clearInterval(fallbackTimer);
  fallbackTimer = setInterval(checkOrientationFallback, 1000);
}
void locService.requestFix();
armOrientationFallback();

// Retry path: re-request motion permission and, if granted, switch back
// from the simulated fallback to the real sensor stream. Simulation is
// never claimed to be real tracking — the diagnostics overlay and HUD
// nudge make the fallback explicit.
async function tryEnableRealOrientation(): Promise<void> {
  const ctor = typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null;
  if (!realOrientation.available) {
    toast("Motion sensors not available on this device.");
    return;
  }
  if (!motionPermissionSupported(ctor)) {
    toast("Motion permission not required — sensors should already be active.");
    return;
  }
  const state = await requestMotionPermission(ctor);
  if (state === "granted") {
    motionPermissionGranted = true;
    motionPermissionDenied = false;
    if (simActive && realOrientation.available) {
      sim.stop();
      simActive = false;
      fallbackReason = null;
      fusion.stop();
      fusion = new OrientationFusion(realOrientation, { smoothing: 0.7, calibration });
      fusion.start();
      orientationStartMs = performance.now();
      armOrientationFallback();
      toast("Motion tracking enabled — the sky now follows your phone.");
    } else {
      toast("Motion permission granted — the sky will track your phone.");
    }
  } else if (state === "denied") {
    motionPermissionDenied = true;
    toast("Motion permission denied — using simulated orientation.", {
      label: "Try again",
      run: () => void tryEnableRealOrientation(),
    });
    armOrientationFallback();
  } else {
    toast("Motion permission unavailable.");
  }
}
// Expose retry for diagnostics / manual testing (dev only, no UI coupling).
(window as unknown as Record<string, unknown>).__tryEnableMotion = tryEnableRealOrientation;

// iOS Safari only delivers deviceorientation after an explicit permission
// grant, and that request must come from a user gesture. Ask on the first
// interaction (a tap anywhere) so the sensors start without requiring the
// user to find the camera button.
if (realOrientation.available && motionPermissionSupported(typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null)) {
  const gesture = (): void => {
    document.removeEventListener("pointerdown", gesture);
    document.removeEventListener("touchstart", gesture);
    void requestMotionPermission(typeof DeviceOrientationEvent !== "undefined" ? DeviceOrientationEvent : null).then((state) => {
      if (state === "denied") {
        motionPermissionDenied = true;
        toast("Motion permission denied — the sky will not track your phone.", {
          label: "Try again",
          run: () => void tryEnableRealOrientation(),
        });
      } else if (state === "granted") {
        motionPermissionGranted = true;
        if (simActive && realOrientation.available) {
          sim.stop();
          simActive = false;
          fallbackReason = null;
          fusion.stop();
          fusion = new OrientationFusion(realOrientation, { smoothing: 0.7, calibration });
          fusion.start();
          orientationStartMs = performance.now();
          armOrientationFallback();
        }
      }
      // Permission resolved: the fallback policy may now be decided (denied
      // devices fall back; granted devices keep the real sensors).
      armOrientationFallback();
    });
  };
  document.addEventListener("pointerdown", gesture);
  document.addEventListener("touchstart", gesture);
}

const labelsEl = document.querySelector<HTMLElement>("#labels")!;
const labelLayer = new LabelLayer(labelsEl);
const hud = new HudLayer(document.querySelector<HTMLElement>("#hud")!);
const info = new InfoCard(document.querySelector<HTMLElement>("#info")!);

// --- field of view (single source of truth, phase 8) -----------------------
// Default 60° vertical FOV. `?fov=NN` overrides for dev/testing.
const fovOverride = (() => {
  const raw = new URLSearchParams(window.location.search).get("fov");
  const parsed = raw === null ? null : Number(raw);
  return parsed !== null && Number.isFinite(parsed) ? parsed : null;
})();
function currentFovYDeg(arMode: boolean): number {
  return resolveFovYDeg(arMode, null, fovOverride);
}

// --- dynamic bodies (Sun/Moon/planets, phase 9) -----------------------------
const bodyCache = createBodyStateCache();
let bodyStates = bodyCache.get(toObserver(locService.get()), nowObserverTime(), Date.now());
let bodyBuffer: { positions: Float32Array; colors: Float32Array } | null = (() => {
  const initHeight = Math.max(1, canvas.clientHeight || 800);
  return {
    positions: bodySpriteData(bodyStates, initHeight, currentFovYDeg(false)),
    colors: bodyColorData(bodyStates),
  };
})();
let labeledBodies: ReturnType<typeof bodyLabels> = bodyLabels(bodyStates);
let lastBodyRefreshKey = "";
function refreshBodies(ctx: FrameContext, heightPx: number): void {
  const next = bodyCache.get(toObserver(ctx.loc), ctx.time, ctx.time.utcMilliseconds);
  const isInitial = bodyBuffer === null;
  if (next === bodyStates && !isInitial) {
    // No ephemeris change, but a resize / FOV switch can still affect sprite sizes.
    const key = `${ctx.loc.latDeg.toFixed(4)},${ctx.loc.lonDeg.toFixed(4)},${ctx.time.utcMilliseconds},${heightPx},${currentFovYDeg(camera.active)}`;
    if (key !== lastBodyRefreshKey) {
      bodyBuffer = {
        positions: bodySpriteData(bodyStates, heightPx, currentFovYDeg(camera.active)),
        colors: bodyColorData(bodyStates),
      };
      lastBodyRefreshKey = key;
    }
    return;
  }
  if (next !== bodyStates) bodyStates = next;
  const key = `${ctx.loc.latDeg.toFixed(4)},${ctx.loc.lonDeg.toFixed(4)},${ctx.time.utcMilliseconds},${heightPx},${currentFovYDeg(camera.active)}`;
  if (isInitial || key !== lastBodyRefreshKey) {
    bodyBuffer = {
      positions: bodySpriteData(bodyStates, heightPx, currentFovYDeg(camera.active)),
      colors: bodyColorData(bodyStates),
    };
    lastBodyRefreshKey = key;
  }
  labeledBodies = bodyLabels(bodyStates);
}

// --- satellite status (reserved provider; no fake positions) ----------------
const satellites = new NoSatelliteProvider();

// --- centered-camera diagnostic (?diag=1&center=Jupiter) --------------------
// Dev-only: when ?diag=1&center=<body> is present, the diagnostic pipeline
// overrides only the camera orientation so the selected body is mathematically
// centered, proving the body can reach visible pixels independent of the
// phone sensor. Real sensor fusion is untouched; the override is only used
// for the diagnostic render/overlay path.
const diagEnabled = new URLSearchParams(window.location.search).get("diag") === "1";
const centerRaw = new URLSearchParams(window.location.search).get("center");
const CENTER_CANDIDATES = ["Sun", "Moon", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune"] as const;
function resolveCenterBody(name: string | null): string | null {
  if (!name) return null;
  const lower = name.trim().toLowerCase();
  for (const c of CENTER_CANDIDATES) if (c.toLowerCase() === lower) return c;
  return null;
}
const centerBodyName: string | null = diagEnabled ? resolveCenterBody(centerRaw) : null;
function centeredQCameraForBody(bodyName: string): import("./orientation/quaternion").Quat | null {
  const state = bodyStateById(bodyStates, `BODY:${bodyName}`);
  if (!state) return null;
  // Do not use syntheticReading (it clamps yaw to ±180 and pitch to -75..85 for
  // drag limits). Diagnostic centering must handle the full 0-360 azimuth and
  // any altitude without clamping, using the same W3C formula as the real
  // sensor path.
  const reading = {
    alphaDeg: (360 - state.azimuthDeg) % 360,
    betaDeg: 90 + state.altitudeDeg,
    gammaDeg: 0,
    absolute: true as const,
    timestampMs: 0,
  };
  const qEarth = deviceToEarthQ(reading);
  if (!qEarth) return null;
  return { x: -qEarth.x, y: -qEarth.y, z: -qEarth.z, w: qEarth.w };
}
function effectiveQCamera(): import("./orientation/quaternion").Quat | null {
  if (centerBodyName) {
    const q = centeredQCameraForBody(centerBodyName);
    if (q) return q;
  }
  return fusion.qCamera;
}

// --- diagnostics overlay (dev only: ?diag=1) ---------------------------------
const diagnosticsEl = document.querySelector<HTMLElement>("#diagnostics");
let diagnostics: DiagnosticsOverlay | null = null;
if (diagnosticsEl) {
  diagnostics = new DiagnosticsOverlay(diagnosticsEl);
  if (diagEnabled) diagnostics.show();
}
// Diagnostic marker for centered body (dev only)
const diagMarkerEl = (() => {
  if (!diagEnabled || !centerBodyName) return null;
  const el = document.createElement("div");
  el.id = "diag-center-marker";
  el.style.cssText =
    "position:fixed;left:0;top:0;width:24px;height:24px;margin:-12px 0 0 -12px;border:2px solid #00ff88;border-radius:50%;box-shadow:0 0 8px #00ff88;pointer-events:none;z-index:21;display:none;";
  document.body.appendChild(el);
  return el;
})();
function syncDiagnostics(frameMs: number): void {
  if (!diagnostics) return;
  const qReal = fusion.qCamera;
  const qEff = effectiveQCamera();
  // Use effective camera for projection when centered diag is active, so the
  // overlay proves the body reaches pixels via the same path the renderer uses.
  const qForDiag = qEff ?? qReal;
  const sizeForDiag = (() => {
    try {
      return session.resize();
    } catch {
      return { widthPx: 640, heightPx: 360 };
    }
  })();
  const widthPx = sizeForDiag.widthPx;
  const heightPx = sizeForDiag.heightPx;
  const fovYDeg = currentFovYDeg(camera.active);
  const aspect = widthPx / Math.max(1, heightPx);
  // Per-body screen/clip diagnostics (same MVP as renderer/labels)
  let bodiesExtended: import("./ui/diagnostics").BodyDiagnostics[] | undefined;
  let centeredScreen: { xPx: number; yPx: number; clipW: number; inFrustum: boolean } | null = null;
  if (cachedCtx && qForDiag) {
    try {
      const labels = bodyLabels(bodyStates);
      const projected = projectLabels(qForDiag, fovYDeg, aspect, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor, widthPx, heightPx, labels);
      const byId = new Map(projected.map((p) => [p.id, p] as const));
      const FAR = 1000;
      const NEAR = 0.1;
      const zScale = (FAR + NEAR) / (FAR - NEAR);
      const proj = perspective(fovYDeg, aspect, NEAR, FAR);
      const mvp = buildStarProjection(proj, qForDiag, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor);
      const updated: import("./ui/diagnostics").BodyDiagnostics[] = [];
      for (const b of bodyStates) {
        const p = byId.get(b.id);
        const v = equatorialVector(b.raHoursJ2000, b.decDegJ2000);
        const clip = transformDir(mvp, v);
        const zView = -clip[2] / zScale;
        const inFront = zView < -NEAR && zView > -FAR;
        const ndcX = inFront ? clip[0] / -zView : 0;
        const ndcY = inFront ? clip[1] / -zView : 0;
        const inFrustum = inFront && Math.abs(ndcX) <= 1.02 && Math.abs(ndcY) <= 1.02;
        const clipW = -zView;
        updated.push({
          name: b.name,
          altitudeDeg: b.altitudeDeg,
          azimuthDeg: b.azimuthDeg,
          xPx: p ? p.xPx : (inFrustum ? ((ndcX + 1) / 2) * widthPx : null),
          yPx: p ? p.yPx : (inFrustum ? ((1 - ndcY) / 2) * heightPx : null),
          clipW,
          inFrustum,
        });
        if (centerBodyName && b.name.toLowerCase() === centerBodyName.toLowerCase()) {
          centeredScreen = {
            xPx: p ? p.xPx : ((ndcX + 1) / 2) * widthPx,
            yPx: p ? p.yPx : ((1 - ndcY) / 2) * heightPx,
            clipW,
            inFrustum,
          };
        }
      }
      bodiesExtended = updated;
      // Update marker position
      if (diagMarkerEl && centeredScreen && centeredScreen.inFrustum) {
        diagMarkerEl.style.left = `${centeredScreen.xPx}px`;
        diagMarkerEl.style.top = `${centeredScreen.yPx}px`;
        diagMarkerEl.style.display = "block";
      } else if (diagMarkerEl) {
        diagMarkerEl.style.display = "none";
      }
    } catch {
      // Fallback: use projectLabels visibility only
      try {
        const labels = bodyLabels(bodyStates);
        const projected = projectLabels(qForDiag!, fovYDeg, aspect, cachedCtx!.loc.latDeg, cachedCtx!.lst, cachedCtx!.rotor!, widthPx!, heightPx!, labels);
        const byId2 = new Map(projected.map((p) => [p.id, p] as const));
        bodiesExtended = bodyStates.map((b) => {
          const p = byId2.get(b.id);
          return {
            name: b.name,
            altitudeDeg: b.altitudeDeg,
            azimuthDeg: b.azimuthDeg,
            xPx: p ? p.xPx : null,
            yPx: p ? p.yPx : null,
            clipW: null,
            inFrustum: p ? p.visible : false,
          };
        });
        if (centerBodyName && bodiesExtended) {
          const c = bodiesExtended.find((x) => x.name.toLowerCase() === centerBodyName.toLowerCase());
          if (c && c.xPx !== null && c.yPx !== null) centeredScreen = { xPx: c.xPx, yPx: c.yPx, clipW: c.clipW ?? 0, inFrustum: c.inFrustum };
        }
        if (diagMarkerEl) diagMarkerEl.style.display = "none";
      } catch {
        if (diagMarkerEl) diagMarkerEl.style.display = "none";
      }
    }
  } else {
    if (diagMarkerEl) diagMarkerEl.style.display = "none";
  }
  const sourceLabel = centerBodyName
    ? `diag-center:${centerBodyName} (simulated)`
    : simActive
      ? `simulated${fallbackReason ? ` (${fallbackReason})` : ""}`
      : `real-${fusion.status}`;
  diagnostics.sync({
    orientationStatus: fusion.status + (centerBodyName ? ` + diag:${centerBodyName}` : ""),
    headingDeg: qEff ? deviceHeadingDeg(qEff) : qReal ? deviceHeadingDeg(qReal) : null,
    tiltDeg: qEff ? deviceTiltDeg(qEff) : qReal ? deviceTiltDeg(qReal) : null,
    qCamera: (qEff ?? qReal) ? `(${(qEff ?? qReal)!.x.toFixed(3)}, ${(qEff ?? qReal)!.y.toFixed(3)}, ${(qEff ?? qReal)!.z.toFixed(3)}, ${(qEff ?? qReal)!.w.toFixed(3)})` : null,
    calibrationOffsetDeg: (calibration.getOffsetRad() * 180) / Math.PI,
    latDeg: cachedCtx ? cachedCtx.loc.latDeg : null,
    lonDeg: cachedCtx ? cachedCtx.loc.lonDeg : null,
    utcMs: cachedCtx ? cachedCtx.time.utcMilliseconds : Date.now(),
    fovYDeg,
    arMode: camera.active,
    framesDrawn,
    frameMs,
    lastFrameMs,
    satelliteReport: satellites.available() ? `${satellites.id} configured` : "SATELLITE DATA SOURCE MISSING",
    bodies: bodyStates,
    bodiesVisible: bodyStates.filter((b) => b.altitudeDeg > 0).map((b) => b.name),
    orientationSource: sourceLabel,
    qCameraNull: (qEff ?? qReal) === null,
    fallbackActive: simActive && fallbackReason !== null,
    fallbackReason,
    simActive,
    bodyCountGpu: bodyBuffer ? bodyBuffer.positions.length / 4 : 0,
    bodiesExtended,
    centeredBody: centerBodyName,
    centeredScreen,
    canvasWidthPx: widthPx,
    canvasHeightPx: heightPx,
  });
}

// window.__sky: instrumentation handle for the runtime harness and manual
// field verification (headless Chrome via scripts/verify-runtime.mjs).
type SkyDebugHandle = {
  status: () => string;
  headingDeg: () => number | null;
  framesDrawn: () => number;
  frameMs: () => number;
  bodies: () => { id: string; name: string; raHoursJ2000: number; decDegJ2000: number; altitudeDeg: number; azimuthDeg: number }[];
  bodyState: (id: string) => ReturnType<SkyDebugHandle["bodies"]>[number] | null;
  satelliteReport: () => string;
  // forensic extensions
  bodyCountGpu: () => number;
  qCameraNull: () => boolean;
  effectiveQCamera: () => string | null;
  fallbackState: () => { simActive: boolean; fallbackReason: string | null; fallbackActive: boolean; source: string };
  diagCenter: () => {
    body: string | null;
    qCamera: string | null;
    qCameraNull: boolean;
    inFrustum: boolean | null;
    screen: { xPx: number; yPx: number; clipW: number } | null;
    approxCenter: boolean | null;
  };
  bodyDiagnostics: () => { name: string; altitudeDeg: number; azimuthDeg: number; xPx: number | null; yPx: number | null; clipW: number | null; inFrustum: boolean }[];
};
declare global {
  interface Window {
    __sky?: SkyDebugHandle;
  }
}
window.__sky = {
  status: () => fusion.status,
  headingDeg: () => {
    const q = effectiveQCamera() ?? fusion.qCamera;
    return q ? deviceHeadingDeg(q) : null;
  },
  framesDrawn: () => framesDrawn,
  frameMs: () => frameMs,
  bodies: () => bodyStates.map((b) => ({ id: b.id, name: b.name, raHoursJ2000: b.raHoursJ2000, decDegJ2000: b.decDegJ2000, altitudeDeg: b.altitudeDeg, azimuthDeg: b.azimuthDeg })),
  bodyState: (id) => {
    const b = bodyStateById(bodyStates, id);
    if (!b) return null;
    return { id: b.id, name: b.name, raHoursJ2000: b.raHoursJ2000, decDegJ2000: b.decDegJ2000, altitudeDeg: b.altitudeDeg, azimuthDeg: b.azimuthDeg };
  },
  satelliteReport: () => satellites.available() ? `${satellites.id} configured` : "SATELLITE DATA SOURCE MISSING",
  bodyCountGpu: () => (bodyBuffer ? bodyBuffer.positions.length / 4 : 0),
  qCameraNull: () => (effectiveQCamera() ?? fusion.qCamera) === null,
  effectiveQCamera: () => {
    const q = effectiveQCamera() ?? fusion.qCamera;
    return q ? `(${q.x.toFixed(3)}, ${q.y.toFixed(3)}, ${q.z.toFixed(3)}, ${q.w.toFixed(3)})` : null;
  },
  fallbackState: () => ({
    simActive,
    fallbackReason,
    fallbackActive: simActive && fallbackReason !== null,
    source: centerBodyName ? `diag-center:${centerBodyName} (simulated)` : simActive ? `simulated${fallbackReason ? ` (${fallbackReason})` : ""}` : `real-${fusion.status}`,
  }),
  diagCenter: () => {
    const q = effectiveQCamera() ?? fusion.qCamera;
    const body = centerBodyName;
    if (!body || !cachedCtx || !q) return { body, qCamera: q ? `(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})` : null, qCameraNull: q === null, inFrustum: null, screen: null, approxCenter: null };
    try {
      const size = session.resize();
      const w = size.widthPx;
      const h = size.heightPx;
      const fov = currentFovYDeg(camera.active);
      const aspect = w / Math.max(1, h);
      const state = bodyStateById(bodyStates, `BODY:${body}`);
      if (!state) return { body, qCamera: `(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})`, qCameraNull: false, inFrustum: null, screen: null, approxCenter: null };
      const labels = bodyLabels(bodyStates);
      const projected = projectLabels(q, fov, aspect, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor, w, h, labels);
      const p = projected.find((x) => x.id === `BODY:${body}`);
      const FAR = 1000;
      const NEAR = 0.1;
      const zScale = (FAR + NEAR) / (FAR - NEAR);
      const proj = perspective(fov, aspect, NEAR, FAR);
      const mvp = buildStarProjection(proj, q, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor);
      const v = equatorialVector(state.raHoursJ2000, state.decDegJ2000);
      const clip = transformDir(mvp, v);
      const zView = -clip[2] / zScale;
      const clipW = -zView;
      const inFrustum = p ? p.visible : zView < -NEAR && zView > -FAR && Math.abs(clip[0] / -zView) <= 1.02 && Math.abs(clip[1] / -zView) <= 1.02;
      const screen = p ? { xPx: p.xPx, yPx: p.yPx, clipW } : inFrustum ? { xPx: ((clip[0] / -zView + 1) / 2) * w, yPx: ((1 - clip[1] / -zView) / 2) * h, clipW } : null;
      const approxCenter = screen ? Math.hypot(screen.xPx - w / 2, screen.yPx - h / 2) < Math.min(w, h) * 0.15 : null;
      return { body, qCamera: `(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})`, qCameraNull: false, inFrustum, screen, approxCenter };
    } catch {
      return { body, qCamera: q ? `(${q.x.toFixed(3)},${q.y.toFixed(3)},${q.z.toFixed(3)},${q.w.toFixed(3)})` : null, qCameraNull: q === null, inFrustum: null, screen: null, approxCenter: null };
    }
  },
  bodyDiagnostics: () => {
    const q = effectiveQCamera() ?? fusion.qCamera;
    if (!cachedCtx || !q) return bodyStates.map((b) => ({ name: b.name, altitudeDeg: b.altitudeDeg, azimuthDeg: b.azimuthDeg, xPx: null, yPx: null, clipW: null, inFrustum: false }));
    try {
      const size = session.resize();
      const w = size.widthPx;
      const h = size.heightPx;
      const fov = currentFovYDeg(camera.active);
      const aspect = w / Math.max(1, h);
      const labels = bodyLabels(bodyStates);
      const projected = projectLabels(q, fov, aspect, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor, w, h, labels);
      const byId = new Map(projected.map((p) => [p.id, p] as const));
      const FAR = 1000;
      const NEAR = 0.1;
      const zScale = (FAR + NEAR) / (FAR - NEAR);
      const proj = perspective(fov, aspect, NEAR, FAR);
      const mvp = buildStarProjection(proj, q, cachedCtx.loc.latDeg, cachedCtx.lst, cachedCtx.rotor);
      return bodyStates.map((b) => {
        const p = byId.get(b.id);
        const v = equatorialVector(b.raHoursJ2000, b.decDegJ2000);
        const clip = transformDir(mvp, v);
        const zView = -clip[2] / zScale;
        const inFront = zView < -NEAR && zView > -FAR;
        const ndcX = inFront ? clip[0] / -zView : 0;
        const ndcY = inFront ? clip[1] / -zView : 0;
        const inFrustum = inFront && Math.abs(ndcX) <= 1.02 && Math.abs(ndcY) <= 1.02;
        return {
          name: b.name,
          altitudeDeg: b.altitudeDeg,
          azimuthDeg: b.azimuthDeg,
          xPx: p ? p.xPx : inFrustum ? ((ndcX + 1) / 2) * w : null,
          yPx: p ? p.yPx : inFrustum ? ((1 - ndcY) / 2) * h : null,
          clipW: -zView,
          inFrustum,
        };
      });
    } catch {
      return bodyStates.map((b) => ({ name: b.name, altitudeDeg: b.altitudeDeg, azimuthDeg: b.azimuthDeg, xPx: null, yPx: null, clipW: null, inFrustum: false }));
    }
  },
};

// --- camera / AR layer ------------------------------------------------------
// The camera is a visual background only: sensors + astronomy + projection
// remain authoritative. The renderer composites transparently over the video
// while `camera.active`; otherwise it renders the photographic skybox.
const cameraVideo = document.querySelector<HTMLVideoElement>("#camera")!;
const camera = new CameraController({
  provider:
    "mediaDevices" in navigator && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function"
      ? { getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c) }
      : null,
  video: cameraVideo,
});
const cameraBtn = document.querySelector<HTMLButtonElement>("#camera-btn")!;
const cameraMessages = new Set<CameraState>();
function syncCameraUi(state: CameraState): void {
  cameraBtn.disabled = state === "unsupported";
  cameraBtn.setAttribute("aria-pressed", String(state === "active"));
  cameraBtn.textContent = state === "active" ? "Hide camera" : "Camera";
  cameraVideo.classList.toggle("active", state === "active");
  if ((state === "denied" || state === "failed") && !cameraMessages.has(state)) {
    cameraMessages.add(state);
    toast(state === "denied" ? "Camera permission denied — showing the sky without it." : "Could not start the camera — showing the sky without it.");
  }
}
camera.onChange(syncCameraUi);
cameraBtn.addEventListener("click", () => {
  if (camera.active) {
    camera.stop();
  } else {
    // iOS motion permission is requested by the first-gesture listener
    // above (the tap is that gesture); here we only start the feed.
    void camera.start();
  }
});
document.addEventListener("visibilitychange", () => camera.handleVisibility(document.hidden));
syncCameraUi(camera.state);
const seeIt = new SeeItOverlay(document.querySelector<HTMLElement>("#seeit")!);
const searchPanel = new SearchPanel(document.querySelector<HTMLElement>("#search")!, {
  onSelect: startSeeIt,
});
document.querySelector<HTMLElement>("#search-btn")!.addEventListener("click", () => {
  searchPanel.toggle();
  document.querySelector<HTMLElement>("#search-btn")!.setAttribute("aria-expanded", String(searchPanel.isOpen));
});

// --- settings + calibration --------------------------------------------------
const calibrateOverlay = new CalibrationOverlay(document.querySelector<HTMLElement>("#calibrate")!, {
  onSetNorth: (headingDeg) => {
    calibration.setOffsetDeg(calibrationOffsetForNorth(headingDeg), "manual");
    calibrateOverlay.hide();
  },
  onCancel: () => calibrateOverlay.hide(),
});
const settings = new SettingsPanel(document.querySelector<HTMLElement>("#settings")!, {
  onToggle: (key, value) => prefs.set({ [key]: value }),
  onCalibrate: () => calibrateOverlay.show(),
  onResetCalibration: () => calibration.clear(),
  onSetManual: (latDeg, lonDeg) => locService.setManual(latDeg, lonDeg),
});
document.querySelector<HTMLElement>("#settings-btn")!.addEventListener("click", () => {
  settings.toggle();
  document.querySelector<HTMLElement>("#settings-btn")!.setAttribute("aria-expanded", String(settings.isOpen));
});
hud.root.addEventListener("click", () => {
  if (fusion.status === "unavailable" && simActive) {
    void tryEnableRealOrientation();
    return;
  }
  if (fusion.status === "relative" || calibration.getOffsetRad() !== 0) calibrateOverlay.show();
});
window.addEventListener("keydown", (e) => {
  if (e.key === "Escape") {
    if (calibrateOverlay.isOpen) calibrateOverlay.hide();
    else if (settings.isOpen) settings.close();
    else stopSeeIt();
  }
});

const LABELS = skyLabels(3, 40, true);

void renderer?.loadTexture(`${import.meta.env.BASE_URL}assets/allsky.png`).then((ok) => {
  if (!ok) console.warn("skybox texture failed to load; rendering without it");
});

// --- SeeIt-mode target ------------------------------------------------------
interface SeeItTarget {
  objectId: string;
  name: string;
  /** J2000 position resolved from the catalog or the ephemeris. */
  raHours: number;
  decDeg: number;
}

let target: SeeItTarget | null = null;

function startSeeIt(result: { objectId: string; name: string }): void {
  const ctx = nowContext();
  const obj = describeObject(result.objectId);
  if (!obj) return;
  const body = bodyFromId(result.objectId);
  let raHours = obj.ra;
  let decDeg = obj.dec;
  if (body) {
    const pos = bodyPositionJ2000(body, toObserver(ctx.loc), ctx.time);
    raHours = pos.raHours;
    decDeg = pos.decDeg;
  }
  target = { objectId: result.objectId, name: result.name, raHours, decDeg };
  info.hide();
  seeIt.show({ name: result.name });
}

function stopSeeIt(): void {
  target = null;
  seeIt.hide();
  info.hide();
}

document.querySelector<HTMLElement>(".seeit-exit")!.addEventListener("click", stopSeeIt);
window.addEventListener("keydown", (e) => {
  if (e.key === "Escape") stopSeeIt();
});

// --- tap-to-identify --------------------------------------------------------
let dragging = false;
let lastX = 0;
let lastY = 0;
let downAtX = 0;
let downAtY = 0;
let downAtT = 0;

canvas.addEventListener("pointerdown", (e) => {
  dragging = true;
  lastX = e.clientX;
  lastY = e.clientY;
  downAtX = e.clientX;
  downAtY = e.clientY;
  downAtT = performance.now();
  canvas.setPointerCapture(e.pointerId);
});
canvas.addEventListener("pointermove", (e) => {
  if (!dragging) return;
  // Only the simulated source accepts drag look-around; with real sensors
  // active the phone's own motion drives the view and drag must not fight it.
  if (simActive) sim.drag(e.clientX - lastX, e.clientY - lastY);
  lastX = e.clientX;
  lastY = e.clientY;
});
function endPointer(e: PointerEvent): void {
  dragging = false;
  const moved = Math.hypot(e.clientX - downAtX, e.clientY - downAtY);
  const held = performance.now() - downAtT;
  if (moved < 10 && held < 400) identifyAt(e.clientX, e.clientY, nowContext());
}
canvas.addEventListener("pointerup", endPointer);
canvas.addEventListener("pointercancel", () => {
  dragging = false;
});
window.addEventListener("resize", () => {
  session.resize();
});

function identifyAt(pixelX: number, pixelY: number, ctx: FrameContext): void {
  if (seeIt.isActive) return;
  const size = session.resize();
  const hit = pickAtPixel({
    pixelX,
    pixelY,
    widthPx: size.widthPx,
    heightPx: size.heightPx,
    fovYDeg: currentFovYDeg(camera.active),
    aspect: size.widthPx / Math.max(1, size.heightPx),
    qCamera: fusion.qCamera,
    latitudeDeg: ctx.loc.latDeg,
    localSiderealHours: ctx.lst,
    time: ctx.time,
    options: { toleranceDeg: 2.5 },
    bodies: bodyStates,
  });
  if (hit) {
    if (hit.type === "body") {
      showBodyCard(hit.id);
    } else {
      info.show(hit);
    }
  } else {
    info.hide();
  }
}

/** Info card for a picked Sun/Moon/planet with live ephemeris meta. */
function showBodyCard(objectId: string): void {
  const obj = describeObject(objectId);
  const body = bodyFromId(objectId);
  if (!obj || !body) return;
  const ctx = nowContext();
  const infoData = bodyDisplayInfo(body, toObserver(ctx.loc), ctx.time);
  const meta: string[] = [];
  if (infoData.magnitude < 90) meta.push(`mag ${infoData.magnitude.toFixed(1)}`);
  if (infoData.distanceAu < 10) meta.push(`${(infoData.distanceAu * 1.495979e8).toFixed(0)} km`);
  else meta.push(`${infoData.distanceAu.toFixed(2)} AU`);
  if (infoData.angularDiameterArcseconds > 0) meta.push(`${infoData.angularDiameterArcseconds.toFixed(0)}″`);
  if (infoData.illuminatedFraction > 0) meta.push(`${Math.round(infoData.illuminatedFraction * 100)}% illuminated`);
  if (body === "Moon" && infoData.moonAgeDays !== null) meta.push(`moon age ${infoData.moonAgeDays.toFixed(1)} d`);
  info.showObject(obj, meta);
}

// --- per-frame context ------------------------------------------------------
function toObserver(loc: GeoLocation): ObserverLocation {
  return {
    latitudeDegrees: loc.latDeg,
    longitudeDegrees: loc.lonDeg,
    elevationMeters: loc.altitudeM ?? undefined,
  };
}

interface FrameContext {
  time: ObserverTime;
  loc: GeoLocation;
  lst: number;
  rotor: { rot: readonly (readonly number[])[] };
}

/** Astronomy context is recomputed at most every 500 ms (perf: the star
 * positions drift far slower than a frame). */
const CONTEXT_REFRESH_MS = 500;
let cachedCtx: FrameContext | null = null;
let cachedCtxAtMs = -Infinity;
function nowContext(nowMs = Date.now()): FrameContext {
  const loc = locService.get();
  if (cachedCtx && loc === cachedCtx.loc && nowMs - cachedCtxAtMs < CONTEXT_REFRESH_MS) {
    return cachedCtx;
  }
  const time = nowObserverTime();
  cachedCtx = {
    time,
    loc,
    lst: (time.siderealHours + loc.lonDeg / 15 + 24) % 24,
    rotor: j2000ToOfDateRotation(time),
  };
  cachedCtxAtMs = nowMs;
  return cachedCtx;
}

// --- per-frame loop --------------------------------------------------------
let framesDrawn = 0;
let frameMs = 0;
let lastFrameMs = 0;
function frame(): void {
  const started = performance.now();
  try {
    stepFrame();
  } catch (err) {
    // A single bad frame must not silently kill the render loop (which would
    // leave the boot overlay visible forever). Report and stop.
    console.error("[startup] fatal error during rendering", err);
    bootError("Rendering failed — try reloading the app.");
    return;
  }
  lastFrameMs = performance.now() - started;
  frameMs = lastFrameMs * 0.9 + (frameMs > 0 ? frameMs : lastFrameMs) * 0.1;
  if (!rendererStartFailed) requestAnimationFrame(frame);
}

function stepFrame(): void {
  const size = session.resize();
  const ctx = nowContext();
  const prefsNow = prefs.get();
  const fovYDeg = currentFovYDeg(camera.active);
  const aspect = size.widthPx / Math.max(1, size.heightPx);

  refreshBodies(ctx, size.heightPx);

  if (session.available && renderer && framesDrawn === 2) hideBoot();
  framesDrawn++;

  const qCamEffective = effectiveQCamera();

  renderer?.frame({
    qCamera: qCamEffective,
    latitudeDeg: ctx.loc.latDeg,
    localSiderealHours: ctx.lst,
    j2000ToOfDate: ctx.rotor,
    fovYDeg,
    aspect,
    showConstellationLines: prefsNow.showConstellationLines,
    showDeepSky: prefsNow.showDeepSky,
    bodies: bodyBuffer,
    arMode: camera.active,
  });

  if (prefsNow.showLabels) {
    const labels = labeledBodies.length > 0 ? [...LABELS, ...labeledBodies] : LABELS;
    const projected = projectLabels(
      qCamEffective,
      fovYDeg,
      aspect,
      ctx.loc.latDeg,
      ctx.lst,
      ctx.rotor,
      size.widthPx,
      size.heightPx,
      labels,
    );
    labelLayer.sync(projected);
  } else {
    labelLayer.sync([]);
  }

  const heading = hud.sync(qCamEffective, size.widthPx);
  let nudge: string | null = null;
  if (centerBodyName) {
    nudge = `diag: centering ${centerBodyName} — simulated view`;
  } else if (fusion.status === "unavailable" && simActive) {
    nudge = fallbackReason ? `using simulated orientation (${fallbackReason}) — tap to enable motion` : "using simulated orientation — tap to enable motion";
  } else if (fusion.status === "relative" && fusion.lastReading?.absolute === false) {
    nudge = "tap the compass to calibrate";
  }
  hud.setNudge(nudge);

  if (calibrateOverlay.isOpen && qCamEffective) {
    calibrateOverlay.sync(heading, heading !== null && headingUsable(deviceTiltDeg(qCamEffective)));
  }
  // The settings panel readouts only matter while it is open (perf: the
  // two textContent writes were running on every frame).
  if (settings.isOpen) {
    settings.sync(prefsNow, (calibration.getOffsetRad() * 180) / Math.PI, ctx.loc);
  }
  syncDiagnostics(lastFrameMs);

  const qForSeeIt = qCamEffective;
  if (target && qForSeeIt) {
    const state = seeItState({
      qCamera: qForSeeIt,
      latitudeDeg: ctx.loc.latDeg,
      localSiderealHours: ctx.lst,
      j2000ToOfDate: ctx.rotor,
      raHours: target.raHours,
      decDeg: target.decDeg,
      fovYDeg,
      aspect,
    });
    syncSeeIt(state, size.widthPx, size.heightPx);
  } else if (seeIt.isActive) {
    seeIt.sync(null, size.widthPx, size.heightPx);
  }
}

function syncSeeIt(state: SeeItState | null, widthPx: number, heightPx: number): void {
  seeIt.sync(state, widthPx, heightPx);
  if (!target) return;
  if (state && state.onScreen && state.offsetRad < 0.12) {
    if (!info.visible) showTargetInfo();
  } else if (!state || !state.onScreen) {
    info.hide();
  }
}

function showTargetInfo(): void {
  if (!target) return;
  const obj = describeObject(target.objectId);
  if (!obj) return;
  const body = bodyFromId(target.objectId);
  if (body) {
    showBodyCard(target.objectId);
  } else {
    info.showObject(obj);
  }
}

if (!rendererStartFailed) requestAnimationFrame(frame);